/**
 * Asignar o quitar responsables de área.
 */
import React, { useState, useEffect } from 'react';
import TrashIcon from '../../assets/icons/trash.svg'; // Asegúrate de tener este icono
import { studio, Field, cols } from './studioUi';
import { statsForRequester, statsForAssignee } from './ReportAdjustmentsPanel';

const ReportResponsiblesModal = ({
    show,
    onClose,
    report,
    responsibles, // Lista de responsables para el reporte actual
    allUsers, // Todos los usuarios del sistema para la selección
    areasList, // Lista de áreas disponibles
    onAddResponsible,
    onRemoveResponsible,
    onUpdateApproval, // Aunque la aprobación se gestiona en ReportDetailView, se pasa por si se necesita aquí
    styles,
    userPermissions,
    currentUserUid,
    adjustments = [],
}) => {
    const [selectedArea, setSelectedArea] = useState('');
    const [selectedUserToAdd, setSelectedUserToAdd] = useState('');
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (show) {
            setSelectedArea('');
            setSelectedUserToAdd('');
            setFilteredUsers([]);
        }
    }, [show]);

    useEffect(() => {
        if (selectedArea && allUsers) {
            // 1. Obtener los UIDs de los usuarios que YA son responsables para el ÁREA SELECCIONADA.
            const assignedUserIdsInArea = responsibles
                .filter(r => r.area === selectedArea)
                .map(r => r.userId);

            // 2. Filtrar la lista de TODOS los usuarios para:
            //    a) Pertenecer al área seleccionada.
            //    b) NO estar ya en la lista de responsables para esa área.
            const availableUsers = allUsers.filter(user => 
                user.area === selectedArea && !assignedUserIdsInArea.includes(user.uid)
            );

            setFilteredUsers(availableUsers);
        } else {
            setFilteredUsers([]);
        }
    }, [selectedArea, allUsers, responsibles]);

    const handleAddResponsibleClick = async () => {
        if (!report || !selectedUserToAdd || !selectedArea) {
            alert('Por favor, selecciona un área y un usuario.');
            return;
        }
        setLoading(true);
        try {
            await onAddResponsible(report.id, selectedUserToAdd, selectedArea);
            setSelectedArea('');
            setSelectedUserToAdd('');
        } catch (error) {
            console.error("Error al añadir responsable:", error);
            alert(`Error al añadir responsable: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveResponsibleClick = async (responsibleId) => {
        if (!report || !responsibleId) return;
        if (!window.confirm('¿Estás seguro de que quieres eliminar a este responsable?')) return;

        setLoading(true);
        try {
            await onRemoveResponsible(report.id, responsibleId);
        } catch (error) {
            console.error("Error al eliminar responsable:", error);
            alert(`Error al eliminar responsable: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    if (!show || !report) return null;

    // Determinar si el usuario actual puede gestionar todos los responsables
    const canManageAllResponsibles = userPermissions.reports_manage_responsibles;

    return (
        <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
                <h3 style={{ color: '#5E35B1', marginBottom: '20px' }}>Gestionar Responsables para: {report.reporteNombre}</h3>

                {loading && <p style={{ textAlign: 'center', color: '#5f6368' }}>Cargando...</p>}

                {/* Sección de Responsables Actuales */}
                <div style={{ marginBottom: '30px', borderBottom: '1px solid #eee', paddingBottom: '20px' }}>
                    <h4>Responsables Asignados ({responsibles.length})</h4>
                    {responsibles.length === 0 ? (
                        <p>No hay responsables asignados a este reporte.</p>
                    ) : (
                        <div style={{ maxHeight: '200px', overflowY: 'auto', paddingRight: '10px' }}>
                            {responsibles.map(r => (
                                <div key={r.id} style={{ ...styles.userCard, justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <img
                                            src={r.userDetails?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(r.userDetails?.nombre || 'N/A')}&background=5E35B1&color=fff&size=30`}
                                            alt="Perfil"
                                            style={styles.miniProfileImage}
                                            referrerPolicy="no-referrer"
                                        />
                                        <div>
                                            <span style={{ fontWeight: 'bold' }}>{r.userDetails?.nombre}</span>
                                            <span style={{ color: '#5f6368', fontSize: '0.85rem', marginLeft: '10px' }}>({r.area})</span>
                                            {(() => {
                                                const asked = statsForRequester(adjustments, r.userId);
                                                const assigned = statsForAssignee(adjustments, r.userId);
                                                return (
                                                    <div style={{ fontSize: '0.78rem', color: '#80868b' }}>
                                                        solicitó {asked.requested} · asignados {assigned.assigned} · pendientes {assigned.pending} · ignorados {assigned.ignored} · resueltos {assigned.resolved}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                    {canManageAllResponsibles && (
                                        <button
                                            onClick={() => handleRemoveResponsibleClick(r.id)}
                                            style={{ ...styles.sBtnR, width: 'auto', height: 'auto', padding: '5px 10px', flexShrink: 0 }}
                                            disabled={loading}
                                        >
                                            <img src={TrashIcon} alt="Quitar" style={{ width: '15px', height: '15px', ...styles.iconImage }} /> Quitar
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Sección para Añadir Responsable */}
                {canManageAllResponsibles && (
                    <div style={{ marginBottom: '30px', borderBottom: '1px solid #eee', paddingBottom: '20px' }}>
                        <h4>Añadir Nuevo Responsable</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: cols('1fr 1fr auto'), gap: '15px', alignItems: 'end' }}>
                            <Field label="Área">
                                <select
                                    value={selectedArea}
                                    onChange={(e) => setSelectedArea(e.target.value)}
                                    style={studio.control}
                                    disabled={loading}
                                >
                                    <option value="">Seleccionar área</option>
                                    {areasList.map(area => (
                                        <option key={area} value={area}>{area}</option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Persona">
                                <select
                                    value={selectedUserToAdd}
                                    onChange={(e) => setSelectedUserToAdd(e.target.value)}
                                    style={studio.control}
                                    disabled={!selectedArea || loading}
                                >
                                    <option value="">Seleccionar usuario</option>
                                    {filteredUsers.map(user => (
                                        <option key={user.uid} value={user.uid}>
                                            {user.nombre} ({user.email})
                                        </option>
                                    ))}
                                </select>
                            </Field>

                            <button
                                onClick={handleAddResponsibleClick}
                                style={styles.btnBlue}
                                disabled={!selectedUserToAdd || !selectedArea || loading}
                            >
                                Añadir
                            </button>
                        </div>
                    </div>
                )}

                {/* Botón de Cerrar */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={styles.btnGray} disabled={loading}>
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReportResponsiblesModal;